import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Text,
  Flex,
  Button,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  VStack,
  Container,
  Center,
  Select,
  HStack,
} from '@chakra-ui/react'
import { FixedSizeGrid } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { ProdutosProvider } from './contexts/ProdutosContext'
import { useConferirTodosOsProdutos } from './hooks/useConferirTodosOsProdutos'
import { ProdutoCard } from './components/ProdutoCard'
import { ProdutoDetailModal } from './components/ProdutoDetailModal'
import { FilterBar } from './components/FilterBar'
import { HeadingPages } from '../../../components/HeadingPages'
import { Pagination } from '../../../components/Pagination'
import './styles.css'

const VirtualizedProdutoCard = React.memo(({ data, columnIndex, rowIndex, style }) => {
  const { produtos, handleProdutoClick, handleDesativarProduto, isDesativando, columnCount } = data
  const index = rowIndex * columnCount + columnIndex

  if (index >= produtos.length) return null

  const produto = produtos[index]

  return (
    <div style={{ ...style, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <ProdutoCard
        key={produto.codigo_produto}
        produto={produto}
        onClick={handleProdutoClick}
        onDesativar={handleDesativarProduto}
        isDesativando={isDesativando === produto.codigo_produto}
      />
    </div>
  )
})

function ConferirTodosOsProdutosContent() {
  const {
    produtos,
    totalProdutos,
    loading,
    isFetching,
    isError,
    refetch,
    desativarProduto,
    isDesativando,
    page,
    setPage,
    totalPages,
    perPage,
    setPerPage,
  } = useConferirTodosOsProdutos()

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBgColor = useColorModeValue('white', 'gray.800')

  const [selectedProduto, setSelectedProduto] = useState(null)
  const { isOpen, onOpen, onClose } = useDisclosure()

  // Mostrar todos os produtos sem paginação quando perPage for igual a totalProdutos
  const showAllProducts = perPage === totalProdutos

  const handleProdutoClick = useCallback(
    (produto) => {
      setSelectedProduto(produto)
      onOpen()
    },
    [onOpen]
  )

  const handleDesativarProduto = useCallback(
    (codigoProduto) => {
      desativarProduto(codigoProduto)
    },
    [desativarProduto]
  )

  const handlePerPageChange = useCallback(
    (e) => {
      setPerPage(Number(e.target.value))
      setPage(1) // Volta para a primeira página ao mudar a quantidade por página
    },
    [setPerPage, setPage]
  )

  useEffect(() => {
    document.title = 'Conferir Todos os Produtos | Innovation CRM'
    return () => {
      document.title = 'Innovation CRM'
    }
  }, [])

  const CARD_WIDTH = 320
  const CARD_HEIGHT = 400
  const GRID_GAP = 16

  const calculateColumnCount = (width) => {
    const availableWidth = width
    return Math.max(1, Math.floor(availableWidth / (CARD_WIDTH + GRID_GAP)))
  }

  const calculateRowCount = (columnCount, itemCount) => {
    return Math.ceil(itemCount / columnCount)
  }

  const calculateGridHeight = () => {
    if (showAllProducts) {
      return Math.min(800, Math.max(400, window.innerHeight * 0.7))
    }

    const itemsPerRow = 4 // Assumindo 4 colunas no desktop
    const rows = Math.min(3, Math.ceil(produtos?.length / itemsPerRow))
    return rows * CARD_HEIGHT + (rows - 1) * GRID_GAP + 20
  }

  return (
    <>
      <HeadingPages name="Conferir Todos os Produtos" />
      <Box p={4} bg={bgColor} minH="calc(100vh - 80px)">
        <Container maxW="1366px" px={0} centerContent>
          <VStack spacing={4} align="stretch" w="100%">
            <FilterBar />

            {isError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle mr={2}>Erro ao carregar produtos!</AlertTitle>
                <AlertDescription>Ocorreu um erro durante a busca dos produtos.</AlertDescription>
                <Button ml="auto" colorScheme="red" size="sm" onClick={refetch}>
                  Tentar novamente
                </Button>
              </Alert>
            )}

            {loading ? (
              <Flex justify="center" align="center" py={16} direction="column">
                <Spinner size="xl" thickness="4px" color="blue.500" mb={4} />
                <Text color="gray.500">Carregando produtos, por favor aguarde...</Text>
              </Flex>
            ) : !produtos || produtos.length === 0 ? (
              <Box bg={cardBgColor} p={8} borderRadius="md" shadow="md" textAlign="center" my={8}>
                <Text fontSize="xl">Nenhum produto encontrado</Text>
                <Text color="gray.500" mt={2}>
                  Tente ajustar seus critérios de busca ou verifique se existem produtos cadastrados.
                </Text>
              </Box>
            ) : (
              <>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontWeight="medium" fontSize="md">
                    <Text as="span" fontWeight="bold" color="blue.600">
                      {totalProdutos}
                    </Text>{' '}
                    produtos encontrados
                  </Text>

                  <HStack spacing={2}>
                    <Text fontSize="sm">Itens por página:</Text>
                    <Select
                      size="sm"
                      value={perPage}
                      onChange={handlePerPageChange}
                      width="auto"
                      defaultValue={totalProdutos} // Define "Todos" como padrão
                    >
                      <option value={totalProdutos}>Todos ({totalProdutos})</option>
                      <option value={100}>100</option>
                      <option value={48}>48</option>
                      <option value={24}>24</option>
                      <option value={20}>20</option>
                      <option value={12}>12</option>
                    </Select>
                  </HStack>
                </Flex>

                {/* Indicador de carregamento durante atualizações */}
                {isFetching && !loading && (
                  <Flex justify="center" py={2}>
                    <Spinner size="sm" color="blue.500" mr={2} />
                    <Text fontSize="sm" color="gray.500">
                      Atualizando resultados...
                    </Text>
                  </Flex>
                )}

                {/* Grid virtualizada para todos os tipos de exibição */}
                <Box
                  w="100%"
                  h={calculateGridHeight()}
                  mb={6}
                  border={showAllProducts ? '1px solid' : 'none'}
                  borderColor="gray.200"
                  borderRadius={showAllProducts ? 'md' : 'none'}
                >
                  <AutoSizer>
                    {({ height, width }) => {
                      const columnCount = calculateColumnCount(width)
                      const rowCount = calculateRowCount(columnCount, produtos.length)

                      return (
                        <FixedSizeGrid
                          columnCount={columnCount}
                          columnWidth={CARD_WIDTH + GRID_GAP}
                          height={height}
                          rowCount={rowCount}
                          rowHeight={CARD_HEIGHT + GRID_GAP}
                          width={width}
                          itemData={{
                            produtos,
                            handleProdutoClick,
                            handleDesativarProduto,
                            isDesativando,
                            columnCount,
                          }}
                          overscanRowCount={2}
                        >
                          {VirtualizedProdutoCard}
                        </FixedSizeGrid>
                      )
                    }}
                  </AutoSizer>
                </Box>

                {/* Paginação - oculta quando estiver mostrando todos os produtos */}
                {!showAllProducts && (
                  <Center py={6}>
                    <Pagination page={page} setPage={setPage} totalPages={totalPages} isDisabled={loading || isFetching} />
                  </Center>
                )}
              </>
            )}
          </VStack>
        </Container>
      </Box>

      {/* Modal de detalhes do produto */}
      <ProdutoDetailModal isOpen={isOpen} onClose={onClose} produto={selectedProduto} />
    </>
  )
}

// Componente principal que fornece o contexto
export function ConferirTodosOsProdutos() {
  return (
    <ProdutosProvider>
      <ConferirTodosOsProdutosContent />
    </ProdutosProvider>
  )
}
